import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  DollarSign, Car, Users, Info, Loader2, Plane, Bus, Truck, Train, 
  Bike, Ship, CircleDot, ArrowRight, ArrowLeft, CreditCard, Save
} from 'lucide-react';
import { toast } from 'sonner';

// Transport types configuration
const TRANSPORT_TYPES = {
  plane: { icon: Plane, label: { 'pt-BR': 'Avião', 'en-US': 'Airplane' } },
  car: { icon: Car, label: { 'pt-BR': 'Carro', 'en-US': 'Car' } },
  bus: { icon: Bus, label: { 'pt-BR': 'Ônibus', 'en-US': 'Bus' } },
  van: { icon: Truck, label: { 'pt-BR': 'Van', 'en-US': 'Van' } },
  train: { icon: Train, label: { 'pt-BR': 'Trem', 'en-US': 'Train' } },
  motorcycle: { icon: Bike, label: { 'pt-BR': 'Moto', 'en-US': 'Motorcycle' } },
  boat: { icon: Ship, label: { 'pt-BR': 'Barco', 'en-US': 'Boat' } },
  other: { icon: CircleDot, label: { 'pt-BR': 'Outro', 'en-US': 'Other' } },
};

// Transport selector component
const TransportSelector = ({ value, onChange, language }) => (
  <div className="grid grid-cols-4 gap-2">
    {Object.entries(TRANSPORT_TYPES).map(([key, config]) => {
      const IconComponent = config.icon;
      const isSelected = value === key;
      return (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          className={`
            flex flex-col items-center gap-1 p-2 sm:p-3 rounded-lg border-2 transition-all
            ${isSelected 
              ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' 
              : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
            }
          `}
        >
          <IconComponent className={`w-4 h-4 sm:w-5 sm:h-5 ${isSelected ? 'text-orange-500' : 'text-slate-500'}`} />
          <span className={`text-[10px] sm:text-xs text-center leading-tight ${isSelected ? 'text-orange-600 font-medium' : 'text-slate-600'}`}>
            {config.label[language] || config.label['pt-BR']}
          </span>
        </button>
      );
    })}
  </div>
);

const TripGeneralSettings = ({ tripId }) => {
  const { t, language } = useLanguage();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    currency: 'BRL',
    shared_transport: false,
    // Shared transport data
    outbound_transport_type: null,
    return_transport_type: null,
    transport_cost: null,
    transport_installments: 1,
    transport_paid: false,
  });

  // Fetch current settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get(`/trips/${tripId}`);
        const tripSettings = response.data.settings || {};
        setSettings({
          currency: tripSettings.currency || 'BRL',
          shared_transport: tripSettings.shared_transport || false,
          outbound_transport_type: tripSettings.outbound_transport_type || null,
          return_transport_type: tripSettings.return_transport_type || null,
          transport_cost: tripSettings.transport_cost || null,
          transport_installments: tripSettings.transport_installments || 1,
          transport_paid: tripSettings.transport_paid || false,
        });
      } catch (error) {
        console.error('Error fetching trip settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [api, tripId]);

  // Update settings
  const updateSettings = async (newSettings) => {
    setSaving(true);
    try {
      await api.put(`/trips/${tripId}/settings`, newSettings);
      setSettings(newSettings);
      toast.success(t('trips.settingsSaved'));
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error(t('trips.settingsError'));
    } finally {
      setSaving(false);
    }
  };

  // Handle single setting change
  const handleSettingChange = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    updateSettings(newSettings);
  };

  // Handle transport settings save
  const handleTransportSave = () => {
    updateSettings(settings);
  };

  // Format currency for display
  const formatCurrency = (value) => {
    if (!value) return '';
    const curr = settings.currency || 'BRL';
    return new Intl.NumberFormat(curr === 'BRL' ? 'pt-BR' : 'en-US', {
      style: 'currency',
      currency: curr,
    }).format(value);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Currency Setting */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-orange-500" />
            {t('trips.generalSettings')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Currency setting */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('trips.currency')}</Label>
            <p className="text-xs text-slate-500">{t('trips.currencyDesc')}</p>
            <Select
              value={settings.currency}
              onValueChange={(value) => handleSettingChange('currency', value)}
              disabled={saving}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BRL">
                  🇧🇷 Real (R$)
                </SelectItem>
                <SelectItem value="USD">
                  🇺🇸 Dólar ($)
                </SelectItem>
                <SelectItem value="EUR">
                  🇪🇺 Euro (€)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Shared Transport Setting */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Car className="w-5 h-5 text-orange-500" />
            {t('trips.sharedTransport')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Toggle for shared transport */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                {t('trips.sharedTransportToggle') || 'Transporte compartilhado para todos'}
              </Label>
              <p className="text-xs text-slate-500">
                {t('trips.sharedTransportDesc')}
              </p>
            </div>
            <Switch
              checked={settings.shared_transport}
              onCheckedChange={(checked) => {
                const newSettings = { 
                  ...settings, 
                  shared_transport: checked,
                  // Clear transport data if disabling
                  ...(checked ? {} : {
                    outbound_transport_type: null,
                    return_transport_type: null,
                    transport_cost: null,
                  })
                };
                updateSettings(newSettings);
              }}
              disabled={saving}
            />
          </div>

          {/* Shared Transport Configuration */}
          {settings.shared_transport && (
            <div className="space-y-6 pt-4 border-t border-slate-200 dark:border-slate-700">
              <Tabs defaultValue="outbound" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="outbound" className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4" />
                    {t('myTrip.transport.outbound') || 'Ida'}
                  </TabsTrigger>
                  <TabsTrigger value="return" className="flex items-center gap-2">
                    <ArrowLeft className="w-4 h-4" />
                    {t('myTrip.transport.return') || 'Volta'}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="outbound" className="space-y-4">
                  <Label className="text-sm font-medium">Tipo de transporte - Ida</Label>
                  <TransportSelector
                    value={settings.outbound_transport_type}
                    onChange={(type) => setSettings({ ...settings, outbound_transport_type: type })}
                    language={language}
                  />
                  {settings.outbound_transport_type && (
                    <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                      {TRANSPORT_TYPES[settings.outbound_transport_type]?.label[language]}
                    </Badge>
                  )}
                </TabsContent>

                <TabsContent value="return" className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Checkbox
                      id="same-transport-settings"
                      checked={settings.return_transport_type === settings.outbound_transport_type && settings.outbound_transport_type}
                      onCheckedChange={(checked) => {
                        if (checked && settings.outbound_transport_type) {
                          setSettings({ ...settings, return_transport_type: settings.outbound_transport_type });
                        }
                      }}
                    />
                    <label htmlFor="same-transport-settings" className="text-sm text-slate-600">
                      {t('myTrip.transport.sameAsOutbound') || 'Mesmo transporte da ida'}
                    </label>
                  </div>
                  <Label className="text-sm font-medium">Tipo de transporte - Volta</Label>
                  <TransportSelector
                    value={settings.return_transport_type}
                    onChange={(type) => setSettings({ ...settings, return_transport_type: type })}
                    language={language}
                  />
                  {settings.return_transport_type && (
                    <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                      {TRANSPORT_TYPES[settings.return_transport_type]?.label[language]}
                    </Badge>
                  )}
                </TabsContent>
              </Tabs>

              {/* Transport Cost */}
              {(settings.outbound_transport_type || settings.return_transport_type) && (
                <div className="space-y-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-orange-500" />
                    <Label className="text-sm font-medium">
                      {t('trips.addTransportCost') || 'Deseja adicionar o custo do transporte?'}
                    </Label>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Valor total</Label>
                      <div className="relative mt-1">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          type="number"
                          placeholder="0,00"
                          value={settings.transport_cost || ''}
                          onChange={(e) => setSettings({ 
                            ...settings, 
                            transport_cost: e.target.value ? parseFloat(e.target.value) : null 
                          })}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Parcelas</Label>
                      <Select
                        value={String(settings.transport_installments || 1)}
                        onValueChange={(val) => setSettings({ ...settings, transport_installments: parseInt(val) })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                            <SelectItem key={n} value={String(n)}>
                              {n}x {settings.transport_cost ? formatCurrency(settings.transport_cost / n) : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="transport-paid"
                      checked={settings.transport_paid}
                      onCheckedChange={(checked) => setSettings({ ...settings, transport_paid: checked })}
                    />
                    <label htmlFor="transport-paid" className="text-sm text-slate-600">
                      {t('myTrip.transportCost.alreadyPaid') || 'Já está pago'}
                    </label>
                  </div>

                  <Alert className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
                    <Info className="w-4 h-4 text-amber-600" />
                    <AlertDescription className="text-sm text-amber-700 dark:text-amber-300">
                      {t('trips.transportCostNote') || 'Os pagamentos serão gerados quando os membros entrarem na viagem'}
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              {/* Save Button */}
              <Button
                onClick={handleTransportSave}
                disabled={saving}
                className="w-full rounded-full bg-orange-500 hover:bg-orange-600"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {t('common.save') || 'Salvar'}
              </Button>
            </div>
          )}

          {/* Info about transport setting */}
          <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <Info className="w-4 h-4 text-blue-600" />
            <AlertDescription className="text-sm text-blue-700 dark:text-blue-300">
              {settings.shared_transport 
                ? (t('trips.sharedTransportInfo') || 'Todos os membros usarão o mesmo transporte definido aqui')
                : (t('trips.individualTransportInfo') || 'Cada membro poderá informar seu próprio transporte')
              }
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
};

export default TripGeneralSettings;
